import type { Block, Blocks, DiffKind } from '@w/content-blocks'
import { orderEdits, slideBoundaries } from './review-diff-cleanup'
import { MAX_DIFF_CELLS, sequenceDiff } from './review-diff-sequence'
import { childrenOf, normalizeText, plainText, textTokens, tokens } from './review-diff-tokens'
import { inlineSegments } from './review-diff-render'
import { codeWordSimilarity, wordDiff } from './review-diff-words'

// Half the lexical weight must survive; punctuation/blank lines cannot establish identity.
const MIN_LINE_SIMILARITY = 0.5

export function codeReview(block: Extract<Block, { type: 'pre' }>, previous: Extract<Block, { type: 'pre' }>): Block {
  let segments = sequenceDiff(codeLines(previous), codeLines(block), line => line)
  // Structural-only islands inside replacements are not reliable anchors.
  segments = orderEdits(segments.flatMap((segment, i) => segment.kind === 'context' && i > 0 && i < segments.length - 1 && segment.values.every(line => !meaningful(line))
    ? [{ kind: 'delete' as const, values: segment.values }, { kind: 'add' as const, values: segment.values }] : [segment]))
  segments = slideBoundaries(segments, line => line, lineBoundary)
  const children: Blocks = []
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index]
    const added = segment.kind === 'delete' && segments[index + 1]?.kind === 'add' ? segments[index + 1] : undefined
    if (added) {
      const pairs = pairLines(segment.values, added.values)
      let oldIndex = 0
      let newIndex = 0
      for (const [oldLine, newLine] of pairs) {
        while (oldIndex < oldLine) appendLine('delete', segment.values[oldIndex++])
        while (newIndex < newLine) appendLine('add', added.values[newIndex++])
        const old = segment.values[oldIndex++]
        const next = added.values[newIndex++]
        const edits = wordDiff(tokens([{ id: '', type: 'text', text: old }]), tokens([{ id: '', type: 'text', text: next }]), true)
        appendLine('context', next, edits.some(edit => edit.kind !== 'context') ? edits : undefined)
      }
      while (oldIndex < segment.values.length) appendLine('delete', segment.values[oldIndex++])
      while (newIndex < added.values.length) appendLine('add', added.values[newIndex++])
      index += 1
    } else {
      for (const line of segment.values) appendLine(segment.kind, line)
    }
  }
  function appendLine(kind: DiffKind, text: string, edits?: ReturnType<typeof wordDiff>) {
    const id = `${block.id}:line${children.length}`
    children.push({ id, type: 'diff', kind, children: edits ? inlineSegments(edits, id) : [{ id: `${id}:text`, type: 'text', text }] })
  }
  return { ...block, children }
}

function codeLines(block: Block): string[] {
  return childrenOf(block).length === 0 ? [] : codeText(block).split('\n')
}

function codeText(block: Block): string {
  const children = childrenOf(block)
  if (children.length > 0 && children.every(child => child.type === 'diff')) {
    return normalizeText(children.flatMap(child => child.kind === 'context' && hasInlineEdits(child)
      ? ['-' + codeSideText(child, 'old'), '+' + codeSideText(child, 'new')]
      : [(child.kind === 'add' ? '+' : child.kind === 'delete' ? '-' : ' ') + plainText(child)]).join('\n'))
  }
  return normalizeText(children.map(child => plainText(child)).join(''))
}

/** Source text for one side of a projected code line, including nested inline edits. */
export function codeSideText(block: Block, side: 'old' | 'new'): string {
  if (block.type === 'diff' && block.kind === (side === 'old' ? 'add' : 'delete')) return ''
  const children = childrenOf(block)
  return children.length > 0 ? children.map(child => codeSideText(child, side)).join('') : plainText(block)
}

function hasInlineEdits(block: Block): boolean {
  return childrenOf(block).some(child => (child.type === 'diff' && child.kind !== 'context') || hasInlineEdits(child))
}

function meaningful(line: string): boolean {
  return /[\p{L}\p{N}]/u.test(line) && !/^\s*end\s*$/u.test(line)
}

function lineBoundary(left: string | undefined, right: string | undefined): number {
  if (left === undefined || right === undefined) return 6
  if (!left.trim()) return 5
  if (/^\s*\/\*\*/u.test(right)) return 3
  if (!right.trim()) return 4
  if (/^\s*[}\])]/u.test(right)) return 0
  if (/^[}\]);,\s]+$/u.test(left)) return 3
  const indent = (line: string) => line.length - line.trimStart().length
  return indent(right) < indent(left) ? 0 : 1
}

/** Monotone weighted alignment, not positional zipping. One budget bounds all
 * lexical comparisons plus the matrix; oversized regions stay plain replacements. */
function pairLines(before: string[], after: string[]): Map<number, number> {
  const pairs = new Map<number, number>()
  const n = before.length
  const m = after.length
  if (n * m > MAX_DIFF_CELLS) return pairs
  const words = (line: string) => textTokens(line).filter(word => /[\p{L}\p{N}]/u.test(word))
  const a = before.map(line => words(line))
  const b = after.map(line => words(line))
  let budget = MAX_DIFF_CELLS - n * m
  const scores = Array.from({ length: n }, () => new Float64Array(m))
  for (let i = 0; i < n; i += 1) {
    for (let j = 0; j < m; j += 1) {
      budget -= before[i].length + after[j].length
      if (budget < 0) return pairs
      if (!meaningful(before[i]) || !meaningful(after[j])) continue
      const counts = new Map<string, number>()
      for (const word of a[i]) counts.set(word, (counts.get(word) ?? 0) + 1)
      let shared = 0
      for (const word of b[j]) {
        const count = counts.get(word) ?? 0
        if (count > 0) { shared += word.length; counts.set(word, count - 1) }
      }
      const total = Math.min(a[i].join('').length, b[j].join('').length)
      let similarity = total ? shared / total : 0
      // A single code identifier may change by an affix; use the same subword rule.
      if (a[i].length === 1 && b[j].length === 1) {
        similarity = Math.max(similarity, codeWordSimilarity(a[i][0], b[j][0]))
      } else if (/^[\d.]+$/u.test(before[i]) && /^[\d.]+$/u.test(after[j])) {
        similarity = Math.max(similarity, codeWordSimilarity(before[i], after[j]))
      }
      if (similarity >= MIN_LINE_SIMILARITY) scores[i][j] = similarity
    }
  }
  const dp = Array.from({ length: n + 1 }, () => new Float64Array(m + 1))
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) dp[i][j] = Math.max(scores[i][j] + dp[i + 1][j + 1], dp[i + 1][j], dp[i][j + 1])
  }
  let i = 0
  let j = 0
  while (i < n && j < m) {
    if (scores[i][j] > 0 && dp[i][j] === scores[i][j] + dp[i + 1][j + 1]) { pairs.set(i, j); i += 1; j += 1 }
    else if (dp[i + 1][j] >= dp[i][j + 1]) i += 1
    else j += 1
  }
  return pairs
}
